import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Col, Row } from 'reactstrap';
import './stat-cards.scss';

/**
 * StatCards — the row of summary stats shown above list pages.
 *
 * ≥768px: one card per stat (icon, label, value) — the classic layout.
 * <768px: a single card holding a two-column grid of label/value pairs with
 *         hairline dividers — dense, scannable, no decorative icons.
 *
 * The stats stay mounted while counts reload; `loading` swaps each value for
 * a shimmering placeholder of the same height so the page never jumps.
 */
const Value = ({ loading, children }) =>
  loading ? (
    <span className="placeholder-wave d-block" aria-busy="true">
      <span className="placeholder col-7 rounded" />
    </span>
  ) : (
    children
  );

Value.propTypes = { loading: PropTypes.bool, children: PropTypes.node };

const StatCards = ({ items = [], loading = false, md = '', className }) => (
  <>
    {/* ---- phones: compact grid in one card ---- */}
    <Card className={['stat-grid-card d-md-none', className].filter(Boolean).join(' ')}>
      <div className="stat-grid">
        {items.map((report, key) => (
          <div className="stat-grid__cell" key={'_cell_' + key}>
            <div className="stat-grid__label text-truncate">{report.title}</div>
            <div className="stat-grid__value">
              <Value loading={loading}>{report.description}</Value>
            </div>
          </div>
        ))}
      </div>
    </Card>

    {/* ---- tablets and up: one card per stat ---- */}
    <Row className={['stat-cards d-none d-md-flex', className].filter(Boolean).join(' ')}>
      {items.map((report, key) => (
        <Col md="4" xl={md} key={'_col_' + key}>
          <Card className="mini-stats-wid">
            <CardBody>
              <div className="d-flex">
                <div className="flex-grow-1 min-w-0">
                  <p className="text-muted fw-medium text-truncate">{report.title}</p>
                  <h4 className="mb-0">
                    <Value loading={loading}>{report.description}</Value>
                  </h4>
                </div>
                <div className="avatar-sm rounded-circle align-self-center mini-stat-icon flex-shrink-0">
                  <span
                    className={`avatar-title rounded-circle ${report.bgColor || 'bg-primary'}`}
                  >
                    <i className={`bx ${report.iconClass} font-size-24`} />
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  </>
);

StatCards.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.node,
      description: PropTypes.node,
      iconClass: PropTypes.string,
      bgColor: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  md: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
};

export default StatCards;
